// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- Direct source consumers include CSS types.
/// <reference path="./styles.d.ts" />
import * as Lucide from "lucide-react";
import { forwardRef } from "react";
import "./Icons.css";

export interface IconProps extends Lucide.LucideProps {
  spin?: boolean;
  rotate?: number;
}

/* @__NO_SIDE_EFFECTS__ */
function icon(Component: Lucide.LucideIcon, filled = false) {
  return forwardRef<SVGSVGElement, IconProps>(function ArkIcon(
    { spin, rotate, className, style, ...props },
    ref,
  ) {
    const named = Boolean(props["aria-label"] || props["aria-labelledby"]);
    return (
      <Component
        aria-hidden={named ? undefined : true}
        focusable="false"
        role={named ? "img" : undefined}
        size="1em"
        strokeWidth={1.7}
        {...(filled ? { fill: "currentColor" } : {})}
        {...props}
        ref={ref}
        className={["ark-icon", spin && "ark-icon-spin", className]
          .filter(Boolean)
          .join(" ")}
        style={{
          ...(rotate === undefined
            ? {}
            : { transform: `rotate(${rotate}deg)` }),
          ...style,
        }}
      />
    );
  });
}

// Source-compatible names keep this migration separate from business behavior.
// Each export renders a Lucide SVG and is tree-shaken when it is unused.
export const AlignCenterOutlined = icon(Lucide.AlignCenter);
export const AlignLeftOutlined = icon(Lucide.AlignLeft);
export const AlignRightOutlined = icon(Lucide.AlignRight);
export const ApartmentOutlined = icon(Lucide.Network);
export const ApiOutlined = icon(Lucide.Plug);
export const AppstoreAddOutlined = icon(Lucide.Grid2X2Plus);
export const AppstoreOutlined = icon(Lucide.Grid2X2);
export const AreaChartOutlined = icon(Lucide.ChartArea);
export const ArrowDownOutlined = icon(Lucide.ArrowDown);
export const ArrowLeftOutlined = icon(Lucide.ArrowLeft);
export const ArrowRightOutlined = icon(Lucide.ArrowRight);
export const ArrowUpOutlined = icon(Lucide.ArrowUp);
export const AudioMutedOutlined = icon(Lucide.MicOff);
export const AudioOutlined = icon(Lucide.Mic);
export const AuditOutlined = icon(Lucide.ClipboardCheck);
export const BarChartOutlined = icon(Lucide.ChartNoAxesColumn);
export const BellOutlined = icon(Lucide.Bell);
export const BgColorsOutlined = icon(Lucide.PaintBucket);
export const BlockOutlined = icon(Lucide.Blocks);
export const BoldOutlined = icon(Lucide.Bold);
export const BookOutlined = icon(Lucide.BookOpen);
export const BranchesOutlined = icon(Lucide.GitBranch);
export const BugOutlined = icon(Lucide.Bug);
export const BuildOutlined = icon(Lucide.Construction);
export const BulbOutlined = icon(Lucide.Lightbulb);
export const CalendarOutlined = icon(Lucide.Calendar);
export const CheckCircleOutlined = icon(Lucide.CircleCheck);
export const CheckOutlined = icon(Lucide.Check);
export const ClockCircleOutlined = icon(Lucide.Clock);
export const CloseCircleOutlined = icon(Lucide.CircleX);
export const CloseOutlined = icon(Lucide.X);
export const CloudDownloadOutlined = icon(Lucide.CloudDownload);
export const CloudOutlined = icon(Lucide.Cloud);
export const CloudServerOutlined = icon(Lucide.Server);
export const CloudSyncOutlined = icon(Lucide.CloudCog);
export const CloudUploadOutlined = icon(Lucide.CloudUpload);
export const ClusterOutlined = icon(Lucide.Network);
export const CodeOutlined = icon(Lucide.Code);
export const ColumnWidthOutlined = icon(Lucide.Columns3);
export const CommentOutlined = icon(Lucide.MessageCircle);
export const ContactsOutlined = icon(Lucide.Contact);
export const CopyOutlined = icon(Lucide.Copy);
export const CreditCardOutlined = icon(Lucide.CreditCard);
export const CrownOutlined = icon(Lucide.Crown);
export const CustomerServiceOutlined = icon(Lucide.Headset);
export const DashboardOutlined = icon(Lucide.Gauge);
export const DatabaseOutlined = icon(Lucide.Database);
export const DeleteColumnOutlined = icon(Lucide.Columns3Cog);
export const DeleteOutlined = icon(Lucide.Trash2);
export const DeleteRowOutlined = icon(Lucide.Rows3);
export const DeploymentUnitOutlined = icon(Lucide.Network);
export const DesktopOutlined = icon(Lucide.Monitor);
export const DisconnectOutlined = icon(Lucide.Unplug);
export const DownOutlined = icon(Lucide.ChevronDown);
export const DownloadOutlined = icon(Lucide.Download);
export const EditOutlined = icon(Lucide.Pencil);
export const ExpandAltOutlined = icon(Lucide.Expand);
export const ExperimentOutlined = icon(Lucide.FlaskConical);
export const ExportOutlined = icon(Lucide.ExternalLink);
export const EyeInvisibleOutlined = icon(Lucide.EyeOff);
export const EyeOutlined = icon(Lucide.Eye);
export const FieldTimeOutlined = icon(Lucide.Timer);
export const FileAddOutlined = icon(Lucide.FilePlus);
export const FileDoneOutlined = icon(Lucide.FileCheck);
export const FileExcelOutlined = icon(Lucide.FileSpreadsheet);
export const FileImageOutlined = icon(Lucide.FileImage);
export const FileMarkdownOutlined = icon(Lucide.FileCode);
export const FileOutlined = icon(Lucide.File);
export const FilePdfOutlined = icon(Lucide.FileText);
export const FilePptOutlined = icon(Lucide.Presentation);
export const FileSearchOutlined = icon(Lucide.FileSearch);
export const FileTextOutlined = icon(Lucide.FileText);
export const FilterOutlined = icon(Lucide.Filter);
export const FolderAddOutlined = icon(Lucide.FolderPlus);
export const FolderOutlined = icon(Lucide.Folder);
export const FullscreenExitOutlined = icon(Lucide.Minimize);
export const FullscreenOutlined = icon(Lucide.Maximize);
export const FunctionOutlined = icon(Lucide.SquareFunction);
export const GlobalOutlined = icon(Lucide.Globe);
export const HeartFilled = icon(Lucide.Heart, true);
export const HeartOutlined = icon(Lucide.Heart);
export const HighlightOutlined = icon(Lucide.Highlighter);
export const HistoryOutlined = icon(Lucide.History);
export const HomeOutlined = icon(Lucide.House);
export const IdcardOutlined = icon(Lucide.IdCard);
export const InboxOutlined = icon(Lucide.Inbox);
export const InfoCircleOutlined = icon(Lucide.Info);
export const InsertRowBelowOutlined = icon(Lucide.BetweenHorizontalStart);
export const ItalicOutlined = icon(Lucide.Italic);
export const KeyOutlined = icon(Lucide.KeyRound);
export const LeftOutlined = icon(Lucide.ChevronLeft);
export const LinkOutlined = icon(Lucide.Link);
export const LockOutlined = icon(Lucide.LockKeyhole);
export const LoginOutlined = icon(Lucide.LogIn);
export const LogoutOutlined = icon(Lucide.LogOut);
export const MailOutlined = icon(Lucide.Mail);
export const MenuFoldOutlined = icon(Lucide.PanelLeftClose);
export const MenuOutlined = icon(Lucide.Menu);
export const MenuUnfoldOutlined = icon(Lucide.PanelLeftOpen);
export const MessageOutlined = icon(Lucide.MessagesSquare);
export const MinusCircleOutlined = icon(Lucide.CircleMinus);
export const MinusOutlined = icon(Lucide.Minus);
export const MobileOutlined = icon(Lucide.Smartphone);
export const MoreOutlined = icon(Lucide.Ellipsis);
export const NodeIndexOutlined = icon(Lucide.GitGraph);
export const NotificationOutlined = icon(Lucide.Megaphone);
export const OrderedListOutlined = icon(Lucide.ListOrdered);
export const PaperClipOutlined = icon(Lucide.Paperclip);
export const PauseCircleOutlined = icon(Lucide.CirclePause);
export const PhoneOutlined = icon(Lucide.Phone);
export const PictureOutlined = icon(Lucide.Image);
export const PieChartOutlined = icon(Lucide.ChartPie);
export const PlayCircleFilled = icon(Lucide.CirclePlay, true);
export const PlayCircleOutlined = icon(Lucide.CirclePlay);
export const PlusOutlined = icon(Lucide.Plus);
export const PoweroffOutlined = icon(Lucide.Power);
export const ReadOutlined = icon(Lucide.BookOpenText);
export const RedoOutlined = icon(Lucide.Redo2);
export const ReloadOutlined = icon(Lucide.RotateCw);
export const RightOutlined = icon(Lucide.ChevronRight);
export const RobotOutlined = icon(Lucide.Bot);
export const RocketOutlined = icon(Lucide.Rocket);
export const RollbackOutlined = icon(Lucide.Undo2);
export const SafetyCertificateOutlined = icon(Lucide.BadgeCheck);
export const SafetyOutlined = icon(Lucide.ShieldCheck);
export const SaveOutlined = icon(Lucide.Save);
export const SearchOutlined = icon(Lucide.Search);
export const SendOutlined = icon(Lucide.Send);
export const SettingOutlined = icon(Lucide.Settings);
export const ShareAltOutlined = icon(Lucide.Share2);
export const SlidersOutlined = icon(Lucide.SlidersHorizontal);
export const SoundOutlined = icon(Lucide.Volume2);
export const StarFilled = icon(Lucide.Star, true);
export const StarOutlined = icon(Lucide.Star);
export const StopOutlined = icon(Lucide.Square);
export const StrikethroughOutlined = icon(Lucide.Strikethrough);
export const SubnodeOutlined = icon(Lucide.GitBranchPlus);
export const SwapOutlined = icon(Lucide.ArrowLeftRight);
export const SyncOutlined = icon(Lucide.RefreshCw);
export const TableOutlined = icon(Lucide.Table2);
export const TagOutlined = icon(Lucide.Tag);
export const TagsOutlined = icon(Lucide.Tags);
export const TeamOutlined = icon(Lucide.Users);
export const ThunderboltOutlined = icon(Lucide.Zap);
export const UnderlineOutlined = icon(Lucide.Underline);
export const UnorderedListOutlined = icon(Lucide.List);
export const UpOutlined = icon(Lucide.ChevronUp);
export const UploadOutlined = icon(Lucide.Upload);
export const UserAddOutlined = icon(Lucide.UserPlus);
export const UserDeleteOutlined = icon(Lucide.UserMinus);
export const UserOutlined = icon(Lucide.UserRound);
export const UserSwitchOutlined = icon(Lucide.UsersRound);
export const UsergroupAddOutlined = icon(Lucide.UserRoundPlus);
export const VideoCameraAddOutlined = icon(Lucide.Video);
export const VideoCameraOutlined = icon(Lucide.Video);
export const WalletOutlined = icon(Lucide.Wallet);
export const WarningOutlined = icon(Lucide.TriangleAlert);
